import React, { useEffect } from "react";
import { Loading } from "@src/common/components";
import {
  Activity,
  useActivitiesQuery,
} from "@api/graphql/generated/generated-types";
import { useErrorToast } from "@src/common/hooks/useErrorToast";
import { useAuth } from "@src/common/hooks/useAuth";
import moment from "moment";
import { ActivityItem } from "./components/ActivityItem";

interface ActivitiesWidgetProps {
  entityId: string;
}

interface GroupedActivities {
  [key: string]: Activity[];
}

function groupActivitiesByDate(activities: Activity[]): GroupedActivities {
  return activities.reduce((groups, activity) => {
    const date = moment(activity.date).format("YYYY-MM-DD");
    if (!groups[date]) {
      groups[date] = [];
    }
    groups[date].push(activity);
    return groups;
  }, {} as GroupedActivities);
}

function ActivitiesWidget({ entityId }: ActivitiesWidgetProps) {
  const { courierId } = useAuth();
  const showError = useErrorToast();

  const {
    data: activitiesData,
    loading: isLoadingActivities,
    refetch,
  } = useActivitiesQuery({
    variables: {
      courierId: courierId!,
      entityId,
    },
    fetchPolicy: "network-only",
    skip: !courierId,
    onError: (error) => {
      showError(error);
    },
  });

  useEffect(() => {
    refetch();
  }, [entityId]);

  const activities = (activitiesData?.activities?.activities ||
    []) as Activity[];
  const groupedActivities = groupActivitiesByDate(activities);

  return (
    <Loading loading={isLoadingActivities} text="Loading activities...">
      <div className="p-3 h-full bg-white dark:bg-gray-800 border dark:border-gray-700 rounded-2xl">
        <span className="font-semibold mb-2 block text-gray-900 dark:text-gray-100">
          Activities
        </span>
        <div className="pt-2 space-y-4">
          {Object.entries(groupedActivities).length > 0 ? (
            Object.entries(groupedActivities).map(([date, activities]) => (
              <div key={date}>
                <h3 className="text-xs font-medium text-gray-400 dark:text-gray-500 mb-1">
                  {moment(date).format("MMMM D, YYYY")}
                </h3>
                <div className="divide-y divide-gray-100 dark:divide-gray-700">
                  {activities.map((activity) => (
                    <ActivityItem key={activity.id} activity={activity} />
                  ))}
                </div>
              </div>
            ))
          ) : (
            <div className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">
              No activities to display.
            </div>
          )}
        </div>
      </div>
    </Loading>
  );
}

export default ActivitiesWidget;
